import { Location } from 'history';
import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { NavigateFunction } from 'react-router-dom';
import { getBootstrapConfig, setWindowLocation } from 'core/window/windowService';
import { NavigationState, SearchPageType } from 'store/slice/slice.types';
import { BootstrapConfig, WebViewQuickShareParameters } from 'core/type/BootstrapConfig.types';
import { getPreviousPageType } from 'core/messaging/analytics/pages/pageTypeUtils';
import { pathname, setHref } from 'core-framework/window/location';
import { RootState } from 'store/store.types';
import { pushState as pushStateToHistory, replaceState as replaceStateInHistory } from 'core-framework/window/history';
import { getApp } from 'core/app/appService';
import { isB } from 'core/window/bootstrapConfig';
import { FunnelExperimentId } from 'core/type/ExperimentId';
import { getCityIdQueryParams } from 'gql/service/graph/activityService';
import { determineSearchPageType } from 'core/utils/pageUtils';

const getInitialState = (config = getBootstrapConfig()): NavigationState => {
    return {
        cid: config.pageConfig?.cid || -1,
        pageTypeId: config.pageConfig?.pageTypeId || -1,
        ssoUrl: config.partner?.ssoUrl || '',
        isMobile: config.browser?.isMobile || false,
        isGlobalKeywordSearchPage: false,
        searchPageType: SearchPageType.None,
    };
};

const navigationSlice = createSlice({
    name: 'search',
    initialState: getInitialState(),
    reducers: {
        setLocation: (state, action: PayloadAction<{ location: Location }>) => {
            const location = action.payload.location;
            state.location = location;
            const cityId = getCityIdQueryParams(location.search);
            const isSearchPage = location.pathname.includes('/search');
            state.isGlobalKeywordSearchPage = isSearchPage && !cityId;
            state.searchPageType = determineSearchPageType();
        },
        // It will only replace URL in browser's address bar. This method will not reload the page.
        setLocationSearch: (state, action: PayloadAction<{ query: string; addToHistory?: boolean; replaceInHistory?: boolean }>) => {
            if (!state.location) return;
            const search = action.payload.query ? `?${action.payload.query}` : '';
            state.location.search = search;

            const currentPath = pathname();
            const currentPathWithUpdatedQuery = `${currentPath}${search}`;
            if (action.payload.replaceInHistory) {
                replaceStateInHistory(currentPathWithUpdatedQuery);
                return;
            }
            if (action.payload.addToHistory) {
                pushStateToHistory(currentPathWithUpdatedQuery);
            }
        },
        // It will only replace URL in browser's address bar as well as reload the state
        setLocationSearchWithNavigation: (state, action: PayloadAction<{ query: string; addToHistory?: boolean; replaceInHistory?: boolean; navigate: NavigateFunction }>) => {
            if (!state.location) return;

            const search = action.payload.query ? `?${action.payload.query}` : '';
            state.location.search = search;

            const currentPath = pathname();
            const currentPathWithUpdatedQuery = `${currentPath}${search}`;

            if (action.payload.replaceInHistory) {
                action.payload.navigate(currentPathWithUpdatedQuery, { replace: true });
                return;
            }
            if (action.payload.addToHistory) {
                action.payload.navigate(currentPathWithUpdatedQuery);
            }
        },
        routeTo: (state, action: PayloadAction<{ path: string; navigate?: NavigateFunction } | string>) => {
            const path = typeof action.payload === 'string' ? action.payload : action.payload.path;
            setHref(path);
        },
        // Remove this method when ACT-4968 is integrated.
        setPageType: (state, action: PayloadAction<{ pageTypeId: number }>) => {
            state.previousPageTypeId = state.pageTypeId;
            state.pageTypeId = action.payload.pageTypeId;
        },
        goBack: (state, action: PayloadAction<{ navigate: NavigateFunction; config: BootstrapConfig }>) => {
            action.payload.navigate(-1);

            // Remove the botton 2 lines whenwhen ACT-4968 is integrated.
            state.previousPageTypeId = state.pageTypeId;
            state.pageTypeId = getPreviousPageType(action.payload.config.browser, state.previousPageTypeId);
        },
    },
});

export const selectNavigationState = (state: RootState) => state.navigation;

// Replace this method with "selectPageTypeIdFromBootstrap" when ACT-4968 is integrated
export const selectPageTypeId = createSelector(
    (state: RootState) => ({ navigation: state.navigation, config: state.bootstrap.config }),
    (state) => {
        const isAct4968 = isB(FunnelExperimentId.ACT_4968, state.config.experiments);
        return isAct4968 ? state.config.pageConfig.pageTypeId : state.navigation.pageTypeId;
    },
);
export const selectCid = createSelector(selectNavigationState, (state) => state.cid);
export const selectIsMobile = createSelector(selectNavigationState, (state) => state.isMobile);
export const selectIsGlobalKeywordSearchPage = createSelector(selectNavigationState, (state) => state.isGlobalKeywordSearchPage);
export const selectSearchPageType = createSelector(selectNavigationState, (state) => state.searchPageType);
export const navigateToBF1 = (bookingFormUrl: string, isWebView: boolean) => {
    isWebView ? getApp().openCartBFWebView(bookingFormUrl) : setWindowLocation(bookingFormUrl);
};

export const openCartPage = () => getApp().openCartPage();
export const openShareSheet = (params: WebViewQuickShareParameters) => getApp().openShareSheet(params);
export const openShareSheetV2 = (url: string, title: string, message: string) => getApp().openShareSheetV2(url, title, message);
export const openPrevNavStack = () => getApp().openPrevNavStack();
export const AGODA_APP_HOME = 'agoda://home';

export const { routeTo, setLocation, setLocationSearch, setPageType, goBack, setLocationSearchWithNavigation } = navigationSlice.actions;
export default navigationSlice.reducer;
